import type { FC } from 'react';

import { useTranslation } from '@crehana/i18n';
import Eye from '@crehana/icons/outline/Eye';
import PlayCircle from '@crehana/icons/outline/PlayCircle';
import Play from '@crehana/icons/Play';
import {
  colors,
  getImgxUrl,
  LazyImageResponsive,
  TextLink,
  TextTag,
  // biome-ignore lint/style/noRestrictedImports: legacy import, will be migrated
} from '@crehana/ui';
import { useUITheme } from '@crehana/web';

import { CourseTypeEnum } from '@/shared/types/graphql/globals';

import {
  CardWrapper,
  HelperWrapper,
  PlayerWrapper,
  PreviewContainer,
  TagWrapper,
} from './PreviewVideo.styles';
import type { PreviewVideoProps } from './types';

const { white } = colors;

const PreviewVideo: FC<PreviewVideoProps> = ({ course, onOpenPreview }) => {
  const { t } = useTranslation();
  const { isDark } = useUITheme();

  const { imagePlaceholder, promoImage } = course;

  const imageCourse = getImgxUrl(promoImage || '', {
    fit: 'fill',
  });

  const isCourseStudio =
    course.courseTypeEnum === CourseTypeEnum.COURSE_TYPE_B2B_CLASSROOMS;

  return (
    <CardWrapper isDark={isDark}>
      <div className="relative rounded-10 min-h-200 md:h-full">
        <LazyImageResponsive
          alt="courseImg"
          className="h-full"
          placeholder={imagePlaceholder}
          style={{ objectPosition: 'center' }}
          imgClassName="object-cover w-full h-full rounded-10"
          sources={{
            default: imageCourse,
            sm: imageCourse,
            md: imageCourse,
            lg: imageCourse,
          }}
          css={`
            div {
              border-radius: 10px;
            }
          `}
        />
        <PreviewContainer
          className="inset-0 opacity-25"
          onClick={isCourseStudio ? () => null : onOpenPreview}
          isStudios={isCourseStudio}
        >
          {isCourseStudio ? (
            <TagWrapper className="tag">
              <TextTag
                label={t('PREVIEW_VIDEO_OWN_COURSE')}
                leftIcon={{
                  component: PlayCircle,
                  size: 16,
                }}
              />
            </TagWrapper>
          ) : (
            <PlayerWrapper className="player">
              <Play size={12} color={white} className="min-w-12" />
            </PlayerWrapper>
          )}
          {!isCourseStudio && (
            <HelperWrapper className="helper">
              <TextLink
                leftIcon={{ component: Eye, size: 20 }}
                className="flex items-center text-white font-link-2 z-1"
              >
                {t('PREVIEW_VIDEO_TRAILER')}
              </TextLink>
            </HelperWrapper>
          )}
        </PreviewContainer>
      </div>
    </CardWrapper>
  );
};

export default PreviewVideo;
